import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ModalOverlay from '../ui/ModalOverlay';
import VideoModelPicker, { defaultPickerValue, type VideoModelPickerValue } from '../video/VideoModelPicker';

type Props = {
  open: boolean;
  prompt?: string;
  modality: 't2v' | 'i2v' | 'v2v';
  recommendedModelId?: string | null;
  preferredModelIds?: string[];
  recommendedHint?: string;
  onConfirm: (value: VideoModelPickerValue) => void;
  onClose: () => void;
};

export default function EditorVideoModelModal({
  open,
  prompt,
  modality,
  recommendedModelId = null,
  preferredModelIds,
  recommendedHint,
  onConfirm,
  onClose,
}: Props) {
  const topId = recommendedModelId || preferredModelIds?.[0] || null;
  const [value, setValue] = useState<VideoModelPickerValue>(() => {
    const next = defaultPickerValue(modality);
    return { ...next, videoModelId: topId || next.videoModelId, durationSeconds: 8 };
  });

  useEffect(() => {
    if (!open) return;
    const next = defaultPickerValue(modality);
    setValue({
      ...next,
      videoModelId: topId || next.videoModelId,
      durationSeconds: Math.max(next.durationSeconds, 8),
    });
  }, [open, modality, topId]);

  if (!open) return null;

  return (
    <ModalOverlay onClose={onClose} closeOnBackdrop={false}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 440, width: 'min(440px, calc(100vw - 32px))' }}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: '1.05rem' }}>Modelo de vídeo</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.62)', lineHeight: 1.45 }}>
          {prompt || 'Escolha o modelo, a qualidade e a duração. Você não precisa saber o nome técnico — o preço aparece abaixo.'}
        </p>
        <VideoModelPicker
          modality={modality}
          value={value}
          onChange={setValue}
          durationSeconds={value.durationSeconds}
          onDurationChange={(seconds) => setValue((prev) => ({ ...prev, durationSeconds: seconds }))}
          recommendedModelId={topId}
          preferredModelIds={preferredModelIds}
          recommendedHint={recommendedHint || 'Sugerido para este pedido'}
          compact
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
              background: 'transparent', color: 'rgba(255,255,255,0.7)',
              fontSize: '0.75rem', padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Agora não
          </button>
          <button
            type="button"
            onClick={() => onConfirm(value)}
            style={{
              border: 'none', borderRadius: 8, background: '#6366f1', color: '#fff',
              fontSize: '0.75rem', padding: '8px 14px', cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: 600,
            }}
          >
            Continuar
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
